// src/pages/Dashboard/components/OwnerCheckIn.jsx
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  New. The owner's two minutes a week, on Today.
//
// YOUR WEEK WITH YOUR MANAGERS.
//
// The owner is the one person in the building nobody gives anything to do,
// and the idea note from 2026-08-27 says why that loses the account: a man who
// only reads a dashboard stops opening it. So once a week this asks him for one
// small act that points down a level: look at whether the process ran, and
// recognise the manager who ran it.
//
// POSITIVE BY DEFAULT. The prompt is to find the thing a manager did right,
// the only button beside a manager gives them a mark, and nothing here grades
// anybody. The facts are the same four the team page shows, computed by the
// same code (storeWeek.js), so the two cannot disagree about a week.
//
// THE MANAGER SEES A MARK AND NOTHING ELSE. That the owner looked, and what he
// looked at, is recorded for him alone, as one owner_checkin row in
// activity_events. A check-in the manager could see would be surveillance with
// a friendly face; a mark from the owner is recognition.
//
// DONE COLLAPSES TO ONE LINE until next Monday, the same shape as the
// salesperson's morning routine, because the point is that he closes the app.

import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, HStack, VStack, Text, Button, Icon, Wrap, WrapItem, useDisclosure } from '@chakra-ui/react'
import { Link as RouterLink } from 'react-router-dom'
import { Check, ChevronRight, RotateCcw } from 'lucide-react'
import { supabase } from '../../../lib/supabase'
import PersonAvatar from '../../Team/PersonAvatar.jsx'
import GiveMark from '../../Manager/GiveMark.jsx'
import { firstNameFor } from '../../../lib/names.js'
import { loadStoreWeek } from '../../../lib/storeWeek'
import { ownerHeadline } from '../../../lib/ownerWeek'
import { listSince, lastMarkFor, dayLabel } from '../../../lib/recognitions'
import { weekStartKey, checkedInThisWeek, recognitionLine, compactWeek } from '../../../lib/ownerCheckIn.js'
import { dayKey, addDays, parseDay } from '../../../lib/localDate'

export default function OwnerCheckIn({ userId, dealershipId }) {
  const today = dayKey()
  const [week, setWeek] = useState(null)
  const [managers, setManagers] = useState(null)
  const [marks, setMarks] = useState(null)
  const [done, setDone] = useState(null) // null unknown, false not yet, row when done
  const [reopened, setReopened] = useState(false)
  const [picked, setPicked] = useState(null)
  const sheet = useDisclosure()

  const load = useCallback(async () => {
    if (!dealershipId || !userId) return
    const since = parseDay(addDays(today, -7)).toISOString()
    const [w, m, r, e] = await Promise.all([
      loadStoreWeek(dealershipId),
      supabase
        .from('users')
        .select('id, display_name, first_name, last_name, initials, role')
        .eq('dealership_id', dealershipId)
        .eq('role', 'manager')
        .order('display_name'),
      listSince(dealershipId, since),
      supabase
        .from('activity_events')
        .select('kind, metadata, created_at')
        .eq('user_id', userId)
        .eq('kind', 'owner_checkin')
        .gte('created_at', parseDay(weekStartKey(today)).toISOString()),
    ])
    setWeek(w)
    setManagers(m.error ? null : m.data || [])
    setMarks(r.data)
    setDone(checkedInThisWeek(e.error ? null : e.data || [], today))
  }, [dealershipId, userId, today])

  useEffect(() => {
    load()
  }, [load])

  if (!dealershipId || week === null) return null

  const finish = async () => {
    const row = { user_id: userId, kind: 'owner_checkin', metadata: { week: weekStartKey(today) } }
    setDone({ ...row, created_at: new Date().toISOString() })
    setReopened(false)
    const { error } = await supabase.from('activity_events').insert(row)
    if (error) console.error('owner check-in: not recorded', error.message)
  }

  const lastFor = picked && marks ? lastMarkFor(picked.id, marks) : null

  return (
    <>
      <OwnerCheckInView
        week={week}
        managers={managers}
        marks={marks}
        done={done}
        reopened={reopened}
        onFinish={finish}
        onReopen={() => setReopened(true)}
        onRecognise={(person) => {
          setPicked(person)
          sheet.onOpen()
        }}
      />
      <GiveMark
        person={picked}
        isOpen={sheet.isOpen}
        onClose={sheet.onClose}
        dealershipId={dealershipId}
        givenBy={userId}
        lastMark={lastFor ? { mark: lastFor.mark, when: dayLabel(lastFor.created_at, today) } : null}
        onGiven={() => load()}
      />
    </>
  )
}

// THE CARD ITSELF, from data it is handed, so the dev preview can draw it.
export function OwnerCheckInView({ week, managers, marks, done, reopened = false, onFinish, onReopen, onRecognise }) {
  if (!week) return null

  // ------------------------------------------------------------- finished
  if (done && !reopened) {
    const when = new Date(done.created_at).toLocaleDateString(undefined, { weekday: 'long' })
    return (
      <HStack bg="card" border="1px solid" borderColor="line" borderRadius="card" px={5} py={4} spacing={4}>
        <Flex boxSize="30px" borderRadius="full" bg="success" color="onSuccess" align="center" justify="center" flexShrink={0}>
          <Icon as={Check} boxSize={4} strokeWidth={3} />
        </Flex>
        <Box flex={1} minW={0}>
          <Text fontWeight={600} letterSpacing="-0.01em">
            You checked in on {when}.
          </Text>
          <Text fontSize="body-sm" color="inkMuted" noOfLines={1}>
            The next one opens on Monday.
          </Text>
        </Box>
        <Button size="sm" variant="ghost" leftIcon={<RotateCcw size={14} />} onClick={onReopen} color="inkDim">
          Open
        </Button>
      </HStack>
    )
  }

  const facts = compactWeek(week.rows)
  const floorIds = (week.floor || []).map((p) => p.id)
  const recog = recognitionLine(marks, floorIds)
  const oneManager = managers && managers.length === 1

  return (
    <Box bg="card" border="1px solid" borderColor="line" borderRadius="cardLg" p={{ base: 5, md: 6 }}>
      <Text textStyle="eyebrow" color="inkDim" mb={1}>
        {oneManager ? 'YOUR WEEK WITH YOUR MANAGER' : 'YOUR WEEK WITH YOUR MANAGERS'}
      </Text>
      <Text fontSize="lg" fontWeight={600} letterSpacing="-0.01em" lineHeight={1.35}>
        {ownerHeadline(week.rows, week.floorSize)}
      </Text>
      <Text fontSize="body-sm" color="inkMuted" mt={1} maxW="measure" lineHeight={1.55}>
        Two minutes, once a week. Find one thing {oneManager ? `${firstNameFor(managers[0]) || 'your manager'} did` : 'a manager did'} right and say so.
      </Text>

      <VStack align="stretch" spacing={5} mt={5}>
        {/* The half minute version of the team page. Same code computes it. */}
        {facts.length > 0 && (
          <Wrap spacing={2}>
            {facts.map((f) => (
              <WrapItem key={f.label}>
                <HStack spacing={2} px={3} py={1.5} borderRadius="pill" bg="surface" border="1px solid" borderColor="line">
                  <Text fontSize="sm" color="inkMuted">{f.label}</Text>
                  <Text fontSize="sm" fontFamily="mono" fontWeight={600}>{f.value}</Text>
                </HStack>
              </WrapItem>
            ))}
          </Wrap>
        )}

        {recog && (
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.5}>
            {recog}
          </Text>
        )}

        {week.error && (
          <Text fontSize="sm" color="warn">
            {week.error}
          </Text>
        )}

        {/* THE ACT. Each manager, and the one thing to do beside them. */}
        {managers && managers.length > 0 && (
          <VStack align="stretch" spacing={0}>
            {managers.map((m) => (
              <HStack key={m.id} py={2.5} borderTop="1px solid" borderColor="line" justify="space-between" spacing={3}>
                <HStack spacing={3} minW={0}>
                  <PersonAvatar person={m} size={40} to={`/team/${m.id}/`} />
                </HStack>
                <Button size="sm" variant="outline" onClick={() => onRecognise?.(m)} flexShrink={0}>
                  Recognise {firstNameFor(m) || 'them'}
                </Button>
              </HStack>
            ))}
          </VStack>
        )}

        {managers && managers.length === 0 && (
          <Text fontSize="body-sm" color="inkMuted" lineHeight={1.5}>
            There is no sales manager on this store yet. Invite one from the team page and this becomes
            your week with them.
          </Text>
        )}

        <Flex gap={3} align="center" wrap="wrap">
          <Button onClick={onFinish} leftIcon={<Check size={15} />}>
            Done for this week
          </Button>
          <Button as={RouterLink} to="/team/" variant="ghost" size="sm" rightIcon={<ChevronRight size={14} />}>
            The whole week
          </Button>
        </Flex>
      </VStack>
    </Box>
  )
}
